// src/services/ItemPurchaseHandler.js - Clean version with proper stock management
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import ReferralCommissionService from "./ReferralCommissionService";

class ItemPurchaseHandler {
  static async processItemPurchase({
    buyerId,
    buyerName,
    buyerEmail,
    item,
    quantity,
    shippingAddress,
    orderComments = "",
    userProfile,
    includesRegistrationFee = false, // Items should never include registration fee
  }) {
    try {
      console.log("Processing item purchase...", {
        buyerId,
        itemId: item.id,
        itemName: item.name,
        quantity,
        includesRegistrationFee,
      });

      // Generate order ID
      const orderId = `ITEM-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      // Calculate amounts - NO registration fee for items
      const itemPrice = item.sellingPrice;
      const itemTotal = itemPrice * quantity;
      const registrationFee = 0; // Always 0 for items
      const totalAmount = itemTotal; // No registration fee added

      // Create order document
      const orderData = {
        orderId,
        type: "item",
        buyerId,
        buyerName,
        buyerEmail,
        itemId: item.id,
        itemName: item.name,
        itemPrice,
        quantity,
        itemTotal,
        registrationFee: 0, // Always 0 for items
        totalAmount,
        paymentStatus: "pending",
        adminConfirmed: false,
        orderStatus: "pending", // pending, confirmed, shipped, delivered, cancelled
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        includesRegistrationFee: false, // Always false for items
        // Item details
        itemDetails: {
          category: item.category,
          description: item.description,
          brand: item.brand || null,
          sizes: item.sizes || [],
          colors: item.colors || [],
          imageUrl: item.imageUrl || null,
        },
        // Shipping details
        shippingAddress: {
          fullName: shippingAddress.fullName,
          addressLine1: shippingAddress.addressLine1,
          addressLine2: shippingAddress.addressLine2,
          city: shippingAddress.city,
          state: shippingAddress.state,
          pincode: shippingAddress.pincode,
          phone: shippingAddress.phone,
        },
        orderComments,
        // User details
        userDetails: {
          referralIdUsed: userProfile?.referralIdUsed || null,
          myReferralId: userProfile?.myReferralId || null,
          isFirstPurchase: false, // Items don't count as "first purchase" for registration
        },
      };

      // Add order to database
      const orderRef = await addDoc(collection(db, "orders"), orderData);
      console.log("Item order created:", orderRef.id);

      // Items do NOT trigger registration fee updates

      // Mark that user has made a purchase (for analytics only)
      await this.markUserAsPurchaser(buyerId, false); // false = not a registration-triggering purchase

      // Process referral commissions (only if user was referred and referrer has purchased packages)
      if (userProfile?.referralIdUsed) {
        await this.processReferralCommissions({
          orderId,
          buyerId,
          buyerName,
          itemTotal,
          referralIdUsed: userProfile.referralIdUsed,
          orderType: "item",
        });
      }

      // Update item stock IMMEDIATELY - this prevents overselling
      await this.updateItemStock(item.id, quantity);

      return {
        success: true,
        orderId,
        orderDocId: orderRef.id,
        totalAmount,
        message: "Item purchase processed successfully",
      };
    } catch (error) {
      console.error("Error processing item purchase:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  static async updateUserRegistrationStatus(userId, paid = true) {
    try {
      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        await updateDoc(userDocRef, {
          "registrationFee.paid": paid,
          "registrationFee.paidAt": paid ? new Date().toISOString() : null,
          updatedAt: new Date().toISOString(),
        });

        console.log("User registration status updated");
      }
    } catch (error) {
      console.error("Error updating user registration status:", error);
    }
  }

  static async markUserAsPurchaser(userId, isFirstPurchase = false) {
    try {
      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
        const updateData = {
          hasMadePurchase: true,
          updatedAt: new Date().toISOString(),
        };

        // If this is their first purchase, record the date
        if (isFirstPurchase) {
          updateData.firstPurchaseAt = new Date().toISOString();
        }

        await updateDoc(userDocRef, updateData);

        console.log("User marked as having made a purchase");
      }
    } catch (error) {
      console.error("Error marking user as purchaser:", error);
    }
  }

  static async updateItemStock(itemId, quantityPurchased) {
    try {
      // Find item document by itemId field (not document ID)
      const itemQuery = query(
        collection(db, "items"),
        where("id", "==", itemId)
      );
      const itemSnapshot = await getDocs(itemQuery);

      if (!itemSnapshot.empty) {
        const itemDocRef = doc(db, "items", itemSnapshot.docs[0].id);
        const currentData = itemSnapshot.docs[0].data();
        const newStock = Math.max(
          0,
          (currentData.stockQuantity || 0) - quantityPurchased
        );

        await updateDoc(itemDocRef, {
          stockQuantity: newStock,
          lastPurchaseAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `Item stock updated immediately: ${currentData.stockQuantity} -> ${newStock} (Reserved ${quantityPurchased} units)`
        );
      } else {
        // If item not found by id field, try finding by document ID
        const itemDocRef = doc(db, "items", itemId);
        const itemDoc = await getDocs(query(collection(db, "items")));
        const matchingItem = itemDoc.docs.find((doc) => doc.id === itemId);

        if (matchingItem) {
          const currentData = matchingItem.data();
          const newStock = Math.max(
            0,
            (currentData.stockQuantity || 0) - quantityPurchased
          );

          await updateDoc(doc(db, "items", itemId), {
            stockQuantity: newStock,
            lastPurchaseAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });

          console.log(
            `Item stock updated (by doc ID): ${currentData.stockQuantity} -> ${newStock}`
          );
        }
      }
    } catch (error) {
      console.error("Error updating item stock:", error);
      // Don't throw error to prevent order creation failure
      // Stock will need to be manually adjusted by admin
    }
  }

  static async processReferralCommissions({
    orderId,
    buyerId,
    buyerName,
    itemTotal,
    referralIdUsed,
    orderType = "item",
  }) {
    try {
      console.log("Processing referral commissions for item purchase...");

      // Find the referrer using the referral ID
      const referrerQuery = query(
        collection(db, "users"),
        where("myReferralId", "==", referralIdUsed)
      );
      const referrerSnapshot = await getDocs(referrerQuery);

      if (referrerSnapshot.empty) {
        console.log("Referrer not found for referral ID:", referralIdUsed);
        return;
      }

      const referrerData = referrerSnapshot.docs[0].data();
      const referrerId = referrerData.uid;

      // Check if referrer has purchased a package (new requirement)
      // Only package purchasers can earn referral commissions
      if (!referrerData.hasPurchasedPackage) {
        console.log(
          "Referrer has not purchased a package, no commission given"
        );
        return;
      }

      // Calculate commission for item purchases (example: 5% of item total, less than package commissions)
      const commissionRate = 0.05; // 5% for items (you can adjust this)
      const commissionAmount = itemTotal * commissionRate;

      // Create commission record
      const commissionData = {
        commissionId: `COMM-${Date.now()}-${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        type: "item_referral",
        orderId,
        orderType,
        referrerId,
        referrerName: referrerData.name,
        referrerEmail: referrerData.email,
        buyerId,
        buyerName,
        itemTotal,
        commissionAmount,
        commissionRate,
        status: "pending", // Will be confirmed when admin confirms the order
        createdAt: new Date().toISOString(),
        notes: `points for item referral: ${referralIdUsed}`,
      };

      // Add commission record
      await addDoc(collection(db, "commissions"), commissionData);
      console.log("Item referral points record created");

      // Update referrer's total points (pending)
      await this.updateReferrerCommissionStats(referrerId, commissionAmount);
    } catch (error) {
      console.error("Error processing referral points:", error);
    }
  }

  static async updateReferrerCommissionStats(referrerId, commissionAmount) {
    try {
      // Find referrer document
      const referrerQuery = query(
        collection(db, "users"),
        where("uid", "==", referrerId)
      );
      const referrerSnapshot = await getDocs(referrerQuery);

      if (!referrerSnapshot.empty) {
        const referrerDocRef = doc(db, "users", referrerSnapshot.docs[0].id);
        const currentData = referrerSnapshot.docs[0].data();

        const currentPendingCommissions = currentData.pendingCommissions || 0;
        const currentTotalCommissions = currentData.totalCommissions || 0;

        await updateDoc(referrerDocRef, {
          pendingCommissions: currentPendingCommissions + commissionAmount,
          totalCommissions: currentTotalCommissions + commissionAmount,
          updatedAt: new Date().toISOString(),
        });

        console.log("Referrer commission stats updated");
      }
    } catch (error) {
      console.error("Error updating referrer commission stats:", error);
    }
  }

  // Method to confirm order and commissions (called by admin)
  static async confirmOrder(orderId) {
    try {
      console.log("Confirming item order:", orderId);

      // Find order
      const orderQuery = query(
        collection(db, "orders"),
        where("orderId", "==", orderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        throw new Error("Order not found");
      }

      const orderDocRef = doc(db, "orders", orderSnapshot.docs[0].id);
      const orderData = orderSnapshot.docs[0].data();

      // Update order status
      await updateDoc(orderDocRef, {
        paymentStatus: "confirmed",
        adminConfirmed: true,
        orderStatus: "confirmed",
        confirmedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // If order included registration fee, confirm user registration
      if (orderData.includesRegistrationFee) {
        await this.confirmUserRegistration(orderData.buyerId);
      }

      // Confirm any related commissions
      await this.confirmCommissions(orderId);

      console.log("Item order confirmed successfully");
      return { success: true };
    } catch (error) {
      console.error("Error confirming item order:", error);
      return { success: false, error: error.message };
    }
  }

  static async confirmUserRegistration(userId) {
    try {
      // Find user document
      const userQuery = query(
        collection(db, "users"),
        where("uid", "==", userId)
      );
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const userDocRef = doc(db, "users", userSnapshot.docs[0].id);

        await updateDoc(userDocRef, {
          "registrationFee.confirmedByAdmin": true,
          "registrationFee.confirmedAt": new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });

        console.log("User registration confirmed by admin");
      }
    } catch (error) {
      console.error("Error confirming user registration:", error);
    }
  }

  static async confirmCommissions(orderId) {
    try {
      // Find commissions for this order
      const commissionsQuery = query(
        collection(db, "commissions"),
        where("orderId", "==", orderId)
      );
      const commissionsSnapshot = await getDocs(commissionsQuery);

      // Update each commission to confirmed status
      for (const commissionDoc of commissionsSnapshot.docs) {
        const commissionRef = doc(db, "commissions", commissionDoc.id);
        await updateDoc(commissionRef, {
          status: "confirmed",
          confirmedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }

      console.log(
        `${commissionsSnapshot.docs.length} item commissions confirmed`
      );
    } catch (error) {
      console.error("Error confirming commissions:", error);
    }
  }

  // Method to restore stock (if order is cancelled by admin)
  static async restoreItemStock(itemId, quantityToRestore) {
    try {
      // Find item document
      const itemQuery = query(
        collection(db, "items"),
        where("id", "==", itemId)
      );
      const itemSnapshot = await getDocs(itemQuery);

      if (!itemSnapshot.empty) {
        const itemDocRef = doc(db, "items", itemSnapshot.docs[0].id);
        const currentData = itemSnapshot.docs[0].data();
        const newStock = (currentData.stockQuantity || 0) + quantityToRestore;

        await updateDoc(itemDocRef, {
          stockQuantity: newStock,
          updatedAt: new Date().toISOString(),
        });

        console.log(
          `Item stock restored: ${currentData.stockQuantity} -> ${newStock} (Restored ${quantityToRestore} units)`
        );
        return { success: true };
      }
      return { success: false, error: "Item not found" };
    } catch (error) {
      console.error("Error restoring item stock:", error);
      return { success: false, error: error.message };
    }
  }

  // Method to cancel order and restore stock
  static async cancelOrder(orderId) {
    try {
      console.log("Cancelling order:", orderId);

      // Find order
      const orderQuery = query(
        collection(db, "orders"),
        where("orderId", "==", orderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        throw new Error("Order not found");
      }

      const orderDocRef = doc(db, "orders", orderSnapshot.docs[0].id);
      const orderData = orderSnapshot.docs[0].data();

      // Update order status to cancelled
      await updateDoc(orderDocRef, {
        orderStatus: "cancelled",
        paymentStatus: "cancelled",
        cancelledAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      // Restore item stock
      if (orderData.type === "item") {
        await this.restoreItemStock(orderData.itemId, orderData.quantity);
      }

      console.log("Order cancelled and stock restored");
      return { success: true };
    } catch (error) {
      console.error("Error cancelling order:", error);
      return { success: false, error: error.message };
    }
  }

  // Method to update order shipping status
  static async updateOrderStatus(orderId, newStatus, trackingInfo = null) {
    try {
      // Find order
      const orderQuery = query(
        collection(db, "orders"),
        where("orderId", "==", orderId)
      );
      const orderSnapshot = await getDocs(orderQuery);

      if (orderSnapshot.empty) {
        throw new Error("Order not found");
      }

      const orderDocRef = doc(db, "orders", orderSnapshot.docs[0].id);
      const updateData = {
        orderStatus: newStatus,
        updatedAt: new Date().toISOString(),
      };

      if (trackingInfo) {
        updateData.trackingInfo = trackingInfo;
      }

      if (newStatus === "shipped") {
        updateData.shippedAt = new Date().toISOString();
      } else if (newStatus === "delivered") {
        updateData.deliveredAt = new Date().toISOString();
      }

      await updateDoc(orderDocRef, updateData);

      console.log(`Order ${orderId} status updated to: ${newStatus}`);
      return { success: true };
    } catch (error) {
      console.error("Error updating order status:", error);
      return { success: false, error: error.message };
    }
  }
}

export default ItemPurchaseHandler;
